/**
 * The two editor preferences the port dropped: **view format** and **text
 * size**.
 *
 * Both were in legacy's settings modal, both sync through the shared settings
 * document, and neither existed anywhere in `/v2` — so an advisor who had set
 * "All pages" and "+3pt" in `/app` opened the new editor and found a single
 * cramped page. The audit table in REMAINING_WORK §3 has carried them as
 * **missing** since the port began.
 *
 * Same shape as `lib/theme.js` on purpose: pure decisions here, storage in
 * `appearance-store.js`, and `/app`'s own localStorage keys written in
 * `/app`'s own format so a change in one program shows in the other without a
 * sync round trip.
 */

/**
 * `original` is one page at a time — legacy's default and the only thing the
 * port ever rendered. `expanded` is every page in one scroll, which is how
 * anybody actually reads a finished itinerary.
 */
export const VIEW_FORMATS = Object.freeze(['original', 'expanded'])

/** Legacy default, app.html:7090. */
export const DEFAULT_VIEW = 'original'

/** The settings key, shared with `/app`. Do not rename. */
export const VIEW_KEY = 'mbm_view'

/** The body class legacy uses. Kept identical so ported CSS keeps working. */
export const EXPANDED_CLASS = 'view-expanded'

/** The settings key for text size, shared with `/app`. */
export const SCALE_KEY = 'mbm_fontScale'

/** Five steps, 0–4. app.html:7836. */
export const MAX_SCALE = 4

/** Legacy's own words, app.html:7836. */
export const SCALE_LABELS = Object.freeze(['Default', '+1pt', '+2pt', '+3pt', '+4pt'])

/**
 * @param {unknown} value
 * @returns {'original'|'expanded'}
 */
export function normalizeView(value) {
  const name = String(value == null ? '' : value).trim().toLowerCase()
  return VIEW_FORMATS.includes(name) ? /** @type {'original'|'expanded'} */ (name) : DEFAULT_VIEW
}

/**
 * A text-size step this app can render.
 *
 * Clamped rather than rejected: legacy stores it as a bare string and
 * `parseInt` will happily produce 97 from a hand-edited document. A clamp
 * gives the advisor the biggest size we have; a reset would silently undo a
 * setting they chose.
 *
 * @param {unknown} value
 * @returns {number}
 */
export function normalizeScale(value) {
  const level = Math.round(Number(String(value == null ? '' : value).trim()))
  if (!Number.isFinite(level)) return 0
  return Math.max(0, Math.min(MAX_SCALE, level))
}

/**
 * @param {unknown} level
 * @returns {string}
 */
export function scaleLabel(level) {
  return SCALE_LABELS[normalizeScale(level)]
}

/**
 * `/app`'s localStorage key for a uid. Its own namespace, bare values, no
 * JSON — see `lib/theme.js#legacyThemeKey` for why that matters.
 *
 * @param {string} uid @param {string} key
 * @returns {string}
 */
export function legacyKey(uid, key) {
  return `u:${uid || 'guest'}:${key}`
}

/**
 * Unwrap a settings value that may be `{v, at}` or bare.
 * @param {any} settings @param {string} key
 * @returns {unknown}
 */
function fromSettings(settings, key) {
  const wrapped = settings && settings[key]
  return wrapped && typeof wrapped === 'object' ? wrapped.v : wrapped
}

/**
 * @param {{settings?: any, legacyLocal?: unknown}} sources
 * @returns {'original'|'expanded'}
 */
export function resolveView(sources = {}) {
  const cloud = fromSettings(sources.settings, VIEW_KEY)
  if (cloud != null && String(cloud).trim() !== '') return normalizeView(cloud)
  if (sources.legacyLocal != null && String(sources.legacyLocal).trim() !== '') {
    return normalizeView(sources.legacyLocal)
  }
  return DEFAULT_VIEW
}

/**
 * @param {{settings?: any, legacyLocal?: unknown}} sources
 * @returns {number}
 */
export function resolveScale(sources = {}) {
  const cloud = fromSettings(sources.settings, SCALE_KEY)
  if (cloud != null && String(cloud).trim() !== '') return normalizeScale(cloud)
  if (sources.legacyLocal != null && String(sources.legacyLocal).trim() !== '') {
    return normalizeScale(sources.legacyLocal)
  }
  return 0
}

/**
 * Put the view format on the page.
 *
 * @param {unknown} view @param {Document} [doc]
 * @returns {'original'|'expanded'}
 */
export function applyView(view, doc = document) {
  const name = normalizeView(view)
  for (const el of [doc.body, doc.documentElement].filter(Boolean)) {
    el.classList.toggle(EXPANDED_CLASS, name === 'expanded')
  }
  return name
}

/**
 * Put the text size on the page.
 *
 * Every level is removed before the current one is added. Legacy does the same
 * (app.html:7850) and it is not defensive padding: the classes are additive in
 * CSS, so a leftover `font-scale-3` under a new `font-scale-1` leaves the
 * advisor on whichever rule happens to be later in the sheet.
 *
 * @param {unknown} level @param {Document} [doc]
 * @returns {number}
 */
export function applyFontScale(level, doc = document) {
  const scale = normalizeScale(level)
  for (const el of [doc.body, doc.documentElement].filter(Boolean)) {
    for (let i = 0; i <= MAX_SCALE; i += 1) el.classList.remove(`font-scale-${i}`)
    if (scale > 0) el.classList.add(`font-scale-${scale}`)
  }
  return scale
}

/**
 * Read `/app`'s copy, if the browser has one.
 * @param {string} uid @param {string} key
 * @returns {string|null}
 */
export function readLegacy(uid, key) {
  try {
    return localStorage.getItem(legacyKey(uid, key))
  } catch {
    return null
  }
}

/**
 * Write `/app`'s copy, so a change in `/v2` shows in `/app` immediately.
 * @param {string} uid @param {string} key @param {unknown} value
 * @returns {boolean}
 */
export function writeLegacy(uid, key, value) {
  try {
    localStorage.setItem(legacyKey(uid, key), String(value))
    return true
  } catch {
    return false
  }
}
